import React, { useState } from 'react';
import { Trash2, Edit, XCircle, PlusCircle, Save, XOctagon } from 'react-feather';
import { api, Variable } from '../api/client';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import { useSnackbar } from '../context/SnackbarContext';

interface Props {
  questionId: string;
  variables: Variable[];
  onVariableAdded: () => void;
}

const VariableManager: React.FC<Props> = ({ questionId, variables, onVariableAdded }) => {
  const { showSnackbar } = useSnackbar();
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<'numeric' | 'categorical' | 'boolean'>('boolean');
  const [dependent, setDependent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteVariable, setDeleteVariable] = useState<Variable | null>(null);

  const [editingVariable, setEditingVariable] = useState<Variable | null>(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editDependent, setEditDependent] = useState(false);
  const [editSubmitting, setEditSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      showSnackbar('Variable name is required.', { variant: 'error' });
      return;
    }

    try {
      setSubmitting(true);
      await api.createVariable(questionId, {
        name: name.trim(),
        description: description.trim() || undefined,
        type,
        dependent,
      });
      setName('');
      setDescription('');
      setType('boolean');
      setDependent(false);
      setShowForm(false);
      showSnackbar('Variable added.', { variant: 'success' });
      onVariableAdded();
    } catch (err) {
      showSnackbar(err instanceof Error ? err.message : 'Failed to create variable', { variant: 'error' });
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteVariable) {
      return;
    }

    try {
      setDeletingId(deleteVariable.id);
      await api.deleteVariable(deleteVariable.id);
      showSnackbar('Variable deleted.', { variant: 'success' });
      setDeleteVariable(null);
      onVariableAdded();
    } catch (err) {
      showSnackbar(err instanceof Error ? err.message : 'Failed to delete variable', { variant: 'error' });
      console.error(err);
    } finally {
      setDeletingId(null);
    }
  };

  const openDeleteConfirm = (variable: Variable) => {
    setDeleteVariable(variable);
  };

  const closeDeleteConfirm = () => {
    if (deletingId) return;
    setDeleteVariable(null);
  };

  const handleEditClick = (v: Variable) => {
    setEditingVariable(v);
    setEditName(v.name);
    setEditDescription(v.description ?? '');
    setEditDependent(v.dependent);
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingVariable) return;
    if (!editName.trim()) {
      showSnackbar('Variable name is required.', { variant: 'error' });
      return;
    }

    try {
      setEditSubmitting(true);
      await api.updateVariable(editingVariable.id, {
        name: editName.trim(),
        description: editDescription.trim() || undefined,
        dependent: editDependent,
      });
      setEditingVariable(null);
      showSnackbar('Variable updated.', { variant: 'success' });
      onVariableAdded();
    } catch (err) {
      showSnackbar(err instanceof Error ? err.message : 'Failed to update variable', { variant: 'error' });
      console.error(err);
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingVariable(null);
  };

  const depVariables = variables.filter(v => v.dependent);
  const indepVariables = variables.filter(v => !v.dependent);

  const getVariableToneStyle = (v: Variable): React.CSSProperties => {
    switch (v.type) {
      case 'numeric':
        return {
          '--variable-accent': 'var(--wnl-data)',
          '--variable-accent-soft': 'var(--wnl-data-tint)',
        } as React.CSSProperties;
      case 'categorical':
        return {
          '--variable-accent': 'var(--wnl-primary)',
          '--variable-accent-soft': 'var(--wnl-primary-tint)',
        } as React.CSSProperties;
      default:
        return {
          '--variable-accent': 'var(--wnl-ok)',
          '--variable-accent-soft': 'var(--wnl-ok-tint)',
        } as React.CSSProperties;
    }
  };

  const getVariableTypeLabel = (type: Variable['type']) => {
    switch (type) {
      case 'numeric':
        return 'Number';
      case 'categorical':
        return 'Category';
      default:
        return 'Yes / No';
    }
  };

  const renderVariable = (v: Variable) => (
    <li
      key={v.id}
      className={`variable-item variable-card ${v.dependent ? 'is-dependent' : 'is-independent'}`}
      style={getVariableToneStyle(v)}
    >
      <div className="variable-info">
        <div className="variable-card-meta">
          <span className="variable-card-dot" aria-hidden="true" />
          <span>{v.dependent ? 'Outcome variable' : 'Predictor variable'}</span>
        </div>
        <div className="variable-header">
          <strong>{v.name}</strong>
          <span className="variable-type">{getVariableTypeLabel(v.type)}</span>
        </div>
        {v.description ? (
          <p className="variable-card-description">{v.description}</p>
        ) : (
          <p className="variable-card-description variable-card-description-empty">
            No description yet.
          </p>
        )}
      </div>
      <div className="row-actions">
        <button
          onClick={() => handleEditClick(v)}
          className="btn-icon"
          disabled={deletingId === v.id}
          title="Edit variable"
        >
          <Edit size={14} />
        </button>
        <button
          onClick={() => openDeleteConfirm(v)}
          className="btn-icon btn-icon-danger"
          disabled={deletingId === v.id}
          title="Delete variable"
        >
          <Trash2 size={14} />
        </button>
      </div>
    </li>
  );

  return (
    <div className="variable-manager">
      <div className="section-header">
        <button onClick={() => setShowForm(true)} className="btn btn-primary btn-sm">
          <PlusCircle size={14} />
          Add variable
        </button>
      </div>
      <div className="section">
        <h3>Dependent Variables (Outcomes)</h3>
        {depVariables.length === 0 ? (
          <p className="empty-state">No dependent variables yet. These represent the outcomes you want to understand.</p>
        ) : (
          <ul className="variable-list">
            {depVariables.map(renderVariable)}
          </ul>
        )}
      </div>

      <div className="section">
        <h3>Independent Variables (Predictors)</h3>
        {indepVariables.length === 0 ? (
          <p className="empty-state">No independent variables yet. These represent the factors you think might affect the outcome.</p>
        ) : (
          <ul className="variable-list">
            {indepVariables.map(renderVariable)}
          </ul>
        )}
      </div>

      {showForm && (
        <div className="modal-overlay" onClick={() => { setShowForm(false); setName(''); setDescription(''); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add variable</h2>
              <button className="btn-close" onClick={() => { setShowForm(false); setName(''); setDescription(''); }}><XCircle size={18} /></button>
            </div>
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-group">
                <label htmlFor="name">Variable Name *</label>
                <input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Dog showed symptoms"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Optional description..."
                  rows={2}
                />
              </div>

              <div className="form-group">
                <label htmlFor="type">Variable Type</label>
                <select
                  id="type"
                  value={type}
                  onChange={(e) => setType(e.target.value as any)}
                >
                  <option value="boolean">Boolean (yes/no)</option>
                  <option value="numeric">Numeric</option>
                  <option value="categorical">Categorical</option>
                </select>
              </div>

              <div className="form-group checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={dependent}
                    onChange={(e) => setDependent(e.target.checked)}
                  />
                  <span>This is a dependent variable (outcome)</span>
                </label>
                <small>Uncheck if this is an independent variable (predictor)</small>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  onClick={() => { setShowForm(false); setName(''); setDescription(''); }}
                  className="btn btn-secondary"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Adding...' : 'Add variable'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editingVariable && (
        <div className="modal-overlay" onClick={handleCancelEdit}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Variable</h2>
              <button className="btn-close" onClick={handleCancelEdit}><XCircle size={18} /></button>
            </div>
            <form onSubmit={handleEditSubmit} className="modal-form">
              <div className="form-group">
                <label htmlFor="edit-name">Variable Name *</label>
                <input
                  id="edit-name"
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="edit-description">Description</label>
                <textarea
                  id="edit-description"
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  rows={2}
                />
              </div>

              <div className="form-group checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={editDependent}
                    onChange={(e) => setEditDependent(e.target.checked)}
                  />
                  <span>This is a dependent variable (outcome)</span>
                </label>
                <small>
                  Type cannot be changed after creation. Current type: <strong>{editingVariable.type}</strong>
                </small>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="btn btn-secondary"
                  disabled={editSubmitting}
                >
                  <XOctagon size={14} />
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={editSubmitting}>
                  <Save size={14} />
                  {editSubmitting ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteVariable && (
        <DeleteConfirmationModal
          title="Delete variable"
          message={`Deleting "${deleteVariable.name}" permanently removes this variable and the values recorded for it in your data points.`}
          confirmLabel="Delete variable"
          submitting={deletingId === deleteVariable.id}
          inputId="variable-delete-confirmation"
          onCancel={closeDeleteConfirm}
          onConfirm={() => void handleDelete()}
        />
      )}
    </div>
  );
};

export default VariableManager;
